import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories, siteInfo } from '@/mocks/comics';

export default function HomePage() {
  const featuredComics = comics.filter((c) => c.featured);
  const trendingComics = [...comics].sort((a, b) => a.trending - b.trending);
  const latestComics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: 'https://manhwaplanet.com',
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://manhwaplanet.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOHead
        title="韩漫星球 - 韩国漫画在线观看平台 | 海量正版韩漫资源"
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl="https://manhwaplanet.com"
        schema={homepageSchema}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20style%20artistic%20illustration%2C%20colorful%20comic%20panels%20collage%20with%20vibrant%20warm%20colors%2C%20manhwa%20aesthetic%20background%2C%20dynamic%20composition%20with%20speech%20bubbles%20and%20action%20lines%2C%20modern%20webcomic%20art%2C%20stylized%20abstract%20geometric%20shapes%20in%20coral%20and%20gold%20tones%2C%20no%20text&width=1920&height=800&seq=hero-bg-main&orientation=landscape"
            alt="韩漫星球 Hero 背景"
            className="w-full h-full object-cover"
            width={1920}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="w-full max-w-[800px] text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
                海量正版韩漫<br className="md:hidden" />尽在<span className="text-accent-400">韩漫星球</span>
              </h1>
              <p className="text-sm md:text-lg text-background-200 mb-8 max-w-xl mx-auto leading-relaxed">
                收录10,000+部正版韩国漫画，涵盖恋爱、动作、奇幻等丰富类型，下拉式流畅阅读，为您打造最安心的韩漫在线观看平台
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/all"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
                >
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-background-50/15 hover:bg-background-50/25 text-background-50 font-medium rounded-full border border-background-50/30 backdrop-blur-sm transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Breadcrumb />

        {/* Stats */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: '正版漫画', value: '10,000+' },
              { label: '累计用户', value: '500万+' },
              { label: '合作作者', value: '3,000+' },
              { label: '日均更新', value: '200+话' },
            ].map((stat) => (
              <div key={stat.label} className="bg-background-100 rounded-xl p-5 text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">{stat.value}</div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">漫画分类</h2>
            <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap">
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {categories.filter(c => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors duration-200 cursor-pointer group"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 group-hover:bg-primary-100 transition-colors duration-200">
                  <i className={`${cat.icon} text-lg text-foreground-600 group-hover:text-primary-600 transition-colors duration-200`}></i>
                </span>
                <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Comics */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">编辑精选</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {featuredComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} variant="featured" />
            ))}
          </div>
        </section>

        {/* Latest Updates + Trending */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Latest Updates */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">最新更新</h2>
                <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap">
                  更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {latestComics.slice(0, 8).map((comic) => (
                  <ComicCard key={comic.id} comic={comic} variant="default" />
                ))}
              </div>
            </div>

            {/* Trending Rankings */}
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">热门排行</h2>
              <div className="bg-background-100 rounded-xl p-4 space-y-1">
                {trendingComics.slice(0, 10).map((comic, index) => (
                  <ComicCard key={comic.id} comic={comic} variant="compact" />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20shapes%2C%20coral%20and%20cream%20tones%2C%20modern%20minimalist%20design%2C%20smooth%20textured%20layers%2C%20elegant%20composition%2C%20no%20text%20or%20logos&width=1920&height=500&seq=cta-bg-home&orientation=landscape"
            alt="CTA背景"
            className="w-full h-full object-cover absolute inset-0"
            width={1920}
            height={500}
          />
          <div className="absolute inset-0 bg-primary-900/60" />
          <div className="relative z-10 max-w-[600px] mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              即刻开启你的韩漫之旅
            </h2>
            <p className="text-sm text-background-200 mb-6">
              加入500万+读者的行列，在韩漫星球发现你最爱的韩国漫画
            </p>
            <Link
              to="/category/all"
              className="inline-flex px-8 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 font-medium rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
            >
              免费试读
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}